/* eslint-disable react-refresh/only-export-components -- useAuth is colocated with AuthProvider */
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { User, UserRole } from '../models/User';

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clientId: string | null;
  organizationId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    // Check localStorage for existing user
    const savedUser = localStorage.getItem('user');
    const savedOrgId = localStorage.getItem('organizationId');
    
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
    }
    if (savedOrgId) {
      setOrganizationId(savedOrgId);
      setClientId(savedOrgId);
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Fetch user document from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            const user: User = {
              ...userData,
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || userData.displayName,
              photoURL: firebaseUser.photoURL || userData.photoURL,
            };

            setCurrentUser(user);
            const effectiveClientId = user.clientId || user.organizationId || null;
            setClientId(effectiveClientId);
            setOrganizationId(effectiveClientId);

            // Save to localStorage
            localStorage.setItem('user', JSON.stringify(user));
            if (effectiveClientId) {
              localStorage.setItem('organizationId', effectiveClientId);
            }
          } else {
            // Create new user document if it doesn't exist
            const newUser: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              role: UserRole.PROJECT_ADMIN,
              createdAt: new Date(),
              updatedAt: new Date(),
            };

            await setDoc(userDocRef, newUser);
            setCurrentUser(newUser);
            localStorage.setItem('user', JSON.stringify(newUser));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setCurrentUser(null);
        setClientId(null);
        setOrganizationId(null);
        localStorage.removeItem('user');
        localStorage.removeItem('organizationId');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setLoading(false);
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
      setClientId(null);
      setOrganizationId(null);
      localStorage.removeItem('user');
      localStorage.removeItem('organizationId');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    loading,
    signIn,
    signOut,
    clientId,
    organizationId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
